/**
 * Unmapped inbox (admin-only): brand_resolution_misses rows that never resolved to a registry
 * brand. This is a queue that gets worked from a phone regularly, so mapping an entry to a
 * brand must cost as few taps as possible -- see BrandPickerSheet.
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, AlertCircle, Inbox, ChevronRight } from 'lucide-react';
import { useAuth } from '@/shared/hooks/useAuth';
import { haptic } from '@/shared/utils/telegram';
import { formatRelativeTime } from '@/shared/utils/formatting';
import {
  useUnmappedMisses,
  useRegistryBrands,
  useMapMiss,
} from '@/features/registry/hooks/useRegistry';
import { BrandPickerSheet } from '@/features/registry/components/BrandPickerSheet';
import type { BrandResolutionMiss, RegistryBrand } from '@/shared/api/registry-api';

export function UnmappedInboxPage() {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [activeMiss, setActiveMiss] = useState<BrandResolutionMiss | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const missesQuery = useUnmappedMisses(isAdmin);
  const brandsQuery = useRegistryBrands(isAdmin);
  const mapMutation = useMapMiss();

  // Server already sorts by hits desc (worst offenders first) -- preserve that order, only
  // drop rows that are already resolved (the endpoint returns all misses, resolved or not).
  const unresolved = useMemo(
    () => (missesQuery.data ?? []).filter((m) => m.resolvedBrandId === null),
    [missesQuery.data],
  );

  const handleBack = () => {
    haptic.impact('light');
    navigate(-1);
  };

  const handleMap = async (brand: RegistryBrand) => {
    if (!activeMiss) return;
    try {
      await mapMutation.mutateAsync({
        rawBrand: activeMiss.rawBrand,
        source: activeMiss.source ?? undefined,
        brandId: brand.id,
      });
      haptic.notification('success');
      setActiveMiss(null);
    } catch {
      haptic.notification('error');
    }
  };

  if (!isAdmin) {
    return (
      <div className="flex flex-col gap-4 pb-20">
        <Header onBack={handleBack} />
        <div className="flex flex-col items-center justify-center py-16 gap-3">
          <AlertCircle size={40} className="text-[var(--tg-theme-hint-color)]" />
          <p className="text-sm font-bold text-[var(--tg-theme-hint-color)]">
            Admin access required
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 pb-24 animate-in fade-in slide-in-from-right duration-300">
      <Header onBack={handleBack} count={unresolved.length} />

      {missesQuery.isLoading && (
        <div className="flex items-center justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-[var(--tg-theme-link-color)] border-t-transparent" />
        </div>
      )}

      {missesQuery.error && (
        <div className="p-6 mx-1 rounded-2xl bg-rose-500/10 border border-rose-500/20 text-center">
          <p className="text-sm font-bold text-rose-600">Failed to load unmapped brands</p>
        </div>
      )}

      {!missesQuery.isLoading && !missesQuery.error && unresolved.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 gap-3">
          <Inbox size={40} className="text-[var(--tg-theme-hint-color)]" />
          <p className="text-sm font-bold text-[var(--tg-theme-hint-color)]">
            Nothing unmapped — inbox is clear
          </p>
        </div>
      )}

      <div className="flex flex-col gap-3">
        {unresolved.map((miss) => {
          const rowKey = `${miss.rawBrand}::${miss.source ?? ''}`;
          const isExpanded = expandedId === rowKey;
          return (
            <div
              key={miss.id}
              className="rounded-2xl bg-[var(--tg-theme-secondary-bg-color)] border border-black/5 dark:border-white/5 overflow-hidden"
            >
              <button
                type="button"
                className="w-full p-4 flex items-center justify-between gap-3 text-left active:scale-[0.99] transition-transform"
                onClick={() => setExpandedId(isExpanded ? null : rowKey)}
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-sm font-bold text-[var(--tg-theme-text-color)] truncate">
                      {miss.rawBrand}
                    </span>
                    <span className="flex-shrink-0 text-[10px] font-black px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-600">
                      {miss.hits} hit{miss.hits === 1 ? '' : 's'}
                    </span>
                  </div>
                  <p className="text-[11px] text-[var(--tg-theme-hint-color)] font-medium mt-0.5">
                    {miss.source ?? 'unknown source'} · first {formatRelativeTime(miss.firstSeen)} ·
                    last {formatRelativeTime(miss.lastSeen)}
                  </p>
                </div>
                <ChevronRight
                  size={16}
                  className={`flex-shrink-0 text-[var(--tg-theme-hint-color)] transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                />
              </button>

              {isExpanded && (
                <div className="px-4 pb-4 space-y-3 animate-in fade-in slide-in-from-top-1 duration-150">
                  {miss.samplePayload != null && (
                    <pre className="text-[10px] leading-relaxed bg-black/5 dark:bg-white/5 rounded-xl p-3 overflow-x-auto whitespace-pre-wrap break-all">
                      {JSON.stringify(miss.samplePayload, null, 2)}
                    </pre>
                  )}
                  <button
                    type="button"
                    onClick={() => setActiveMiss(miss)}
                    className="w-full py-2.5 px-4 bg-[var(--tg-theme-link-color)] text-white rounded-xl text-xs font-bold uppercase tracking-wide active:scale-[0.98] transition-transform"
                  >
                    Map to brand
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <BrandPickerSheet
        isOpen={activeMiss !== null}
        onClose={() => setActiveMiss(null)}
        brands={brandsQuery.data ?? []}
        isLoading={brandsQuery.isLoading}
        onSelect={handleMap}
        pendingBrandId={mapMutation.isPending ? mapMutation.variables?.brandId : undefined}
      />
    </div>
  );
}

function Header({ onBack, count }: { onBack: () => void; count?: number }) {
  return (
    <div className="sticky top-0 z-10 bg-[var(--tg-theme-bg-color)] pt-2 pb-2 flex items-center gap-3">
      <button
        onClick={onBack}
        className="p-2 rounded-full hover:bg-[var(--tg-theme-secondary-bg-color)] transition-colors"
        aria-label="Back"
      >
        <ArrowLeft size={20} className="text-[var(--tg-theme-hint-color)]" />
      </button>
      <h1 className="text-2xl font-black uppercase tracking-tighter flex-1">Unmapped Inbox</h1>
      {typeof count === 'number' && count > 0 && (
        <span className="text-xs font-black px-2 py-1 rounded-full bg-rose-500/15 text-rose-600">
          {count}
        </span>
      )}
    </div>
  );
}
